<script>
  import { onMount } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import ScatterChart from './cgm/ScatterChart.svelte';
  import EventTimeline from './cgm/EventTimeline.svelte';
  import EventDetailsModal from './cgm/EventDetailsModal.svelte';
  import AddSubjectFormButton from '$lib/AddSubjectFormButton.svelte';
  import ParticipantDetails from '$lib/ParticipantDetails.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import Pagination from '$lib/Pagination.svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { studyProperties, isFeatureEnabled, fetchStudyProperties } from '$stores/configStore';
  import { config, delayHours } from '$stores/configStore';
  import { glucoseunit } from '$stores/unitsStore';
  import { convertGlucoseValue } from '$utils/unitUtils';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '$lib/Footer.svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import {
    faChevronUp,
    faChevronDown,
    faPlay,
    faRotate,
    faCalendarDay,
    faBell,
    faFileLines,
  } from '@fortawesome/pro-light-svg-icons';
  import RawModal from './cgm/RawModal.svelte';
  import { page } from '$stores/pageStore';
  import DailyReadingsCards from '$lib/DailyReadingsCards.svelte';
  import PrintButton from '$lib/PrintButton.svelte';
  const API_BASE_URL = '/api/proxy';

  import { exportToCSV, exportToExcel, generateExportFilename } from '$utils/export.js';
  import { formatToLocalDateTime } from '$utils/dateUtils.js';

  // Feature flag variables
  let isSMBGEnabled = true;
  let isDiaryEnabled = true;
  let isMessagingEnabled = true;

  // Feature flag for measurements report card view
  let isMeasurementsReportCardViewEnabled = true;

  // Reactive variables for feature flags
  $: {
    isSMBGEnabled = isFeatureEnabled('smbg');
    isDiaryEnabled = isFeatureEnabled('diary');
    isMessagingEnabled = isFeatureEnabled('messaging');
    isMeasurementsReportCardViewEnabled = isFeatureEnabled('cardViews.measurementsReport');
  }

  // Subscribe to selectedStudy changes to fetch properties
  $: if ($selectedStudy?._id) {
    fetchStudyProperties($selectedStudy._id);
  } else {
    studyProperties.set(null);
  }

  let forms = [];
  let messages = [];
  let events = [];
  let cgms = [];
  let compliancesCount = 0;
  let currentPage = 1;
  let totalPages = 1;
  let limit = 20;
  let scatterData = [];
  let chartCgms = [];
  let isLoading = false;
  let chartVisible = true;
  let inRange = true;
  let selectedType = 'ALL';
  let isModalOpen = false;
  let selectedRawData = '';
  let selectedDate = '';
  let isEventModalOpen = false;
  let selectedEvent = null;
  let hoveredEvent = null;
  let activeTableView = 'measurements';
  let activeTableCount = 0;
  let formDatePriority = 'formDate';
  let isEventTimelineEnabled = false;
  let hasInitializedTableView = false;

  const TABLE_VIEWS = {
    MEASUREMENTS: 'measurements',
    EVENTS: 'events',
  };

  $: formDatePriority = $studyProperties?.reports?.reportSettings?.diaryEntryDatePriority || 'formDate';
  $: isEventTimelineEnabled = $studyProperties?.features?.eventTimeline === true;
  $: if ($studyProperties && !hasInitializedTableView) {
    activeTableView = isEventTimelineEnabled ? TABLE_VIEWS.EVENTS : TABLE_VIEWS.MEASUREMENTS;
    hasInitializedTableView = true;
  }
  $: if (!isEventTimelineEnabled && activeTableView === TABLE_VIEWS.EVENTS) {
    activeTableView = TABLE_VIEWS.MEASUREMENTS;
  }

  function sanitizeJsonString(raw) {
    if (!raw || typeof raw !== 'string') return raw;
    return raw.replace(/\r?\n/g, '\\n');
  }

  function isValidDate(dateString) {
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/.test(dateString);
  }

  function flattenData(data) {
    const result = [];

    if (Array.isArray(data)) {
      data.forEach((item) => {
        if (item.value && typeof item.value === 'object' && !Array.isArray(item.value)) {
          for (const [key, value] of Object.entries(item.value)) {
            if (value === true) {
              result.push({ component_name: `${item.component_name}.${key}`, value });
            }
          }
        } else if (Array.isArray(item.value)) {
          item.value.forEach((code) => {
            result.push({ component_name: `${item.component_name}.${code}`, value: true });
          });
          result.push({ component_name: item.component_name, value: item.value, _isArray: true });
        } else if (item.values && Array.isArray(item.values)) {
          item.values.forEach((subArray, index) => {
            subArray.forEach((subItem) => {
              result.push({
                component_name: `${item.section_name}[${index}].${subItem.component_name}`,
                value: subItem.value,
              });
            });
          });
        } else if (typeof item.value === 'string' && isValidDate(item.value)) {
          result.push({ ...item, value: new Date(item.value) });
        } else if (typeof item.value === 'string') {
          result.push({ ...item, value: item.value.replace(/\n/g, '') });
        } else {
          result.push(item);
        }
      });
    } else if (typeof data === 'object' && data !== null) {
      for (const [key, value] of Object.entries(data)) {
        result.push({ component_name: key, value });
      }
    }

    return result;
  }

  function stripHtmlTags(text) {
    if (!text) return '';
    return text.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  }

  function parseFormConfig(rawFormConfig) {
    if (!rawFormConfig) return null;
    try {
      const parsed =
        typeof rawFormConfig === 'string'
          ? JSON.parse(sanitizeJsonString(rawFormConfig))
          : rawFormConfig;

      if (parsed?.section?.sections) {
        return parsed;
      }

      if (parsed?.type === 'sections' && Array.isArray(parsed.sections)) {
        return { ...parsed, section: parsed };
      }

      return parsed;
    } catch {
      return null;
    }
  }

  function extractFieldLabelsFromForm(formDefinition) {
    if (!formDefinition) {
      return {};
    }

    try {
      const formDef = parseFormConfig(formDefinition);
      if (!formDef?.section) {
        return {};
      }

      const fieldLabels = {};

      function extractFields(section) {
        if (!section?.sections) {
          return;
        }

        section.sections.forEach((subSection) => {
          if (subSection.type === 'component' && subSection.component) {
            const component = subSection.component;
            if (component.name) {
              const labelText = component.header || component.placeholder || component.label;
              if (labelText) {
                fieldLabels[component.name] = stripHtmlTags(labelText);
              }
              if (component.configuration && Array.isArray(component.configuration.options)) {
                component.configuration.options.forEach((opt) => {
                  if (opt?.code) {
                    fieldLabels[`${component.name}.${opt.code}`] = opt.label || opt.code;
                  }
                });
              }
            }
          } else if (subSection.sections) {
            extractFields(subSection);
          }
        });
      }

      extractFields(formDef.section);
      return fieldLabels;
    } catch {
      return {};
    }
  }

  function getEnglishFormDefinition(formDefinition) {
    if (!formDefinition) return null;

    if (formDefinition.translations && Array.isArray(formDefinition.translations)) {
      const englishTranslation = formDefinition.translations.find(
        (translation) => translation.languageCode === 'en_US'
      );

      if (englishTranslation?.form) {
        return englishTranslation.form;
      }

      if (formDefinition.translations[0]?.form) {
        return formDefinition.translations[0].form;
      }
    }

    return formDefinition.form || null;
  }

  function getEnglishLabelsFromFD(formDefinition) {
    const englishForm = getEnglishFormDefinition(formDefinition);
    return englishForm ? extractFieldLabelsFromForm(englishForm) : {};
  }

  function formatLabelFromComponentName(componentName) {
    if (!componentName) {
      return 'Field';
    }

    const finalSegment = componentName.split('.').pop() || componentName;
    return finalSegment
      .replace(/\[(\d+)\]/g, ' $1 ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/_/g, ' ')
      .replace(/^./, (char) => char.toUpperCase())
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/, specify$/i, '');
  }

  function getFieldLabel(componentName, labelsMap = {}) {
    return labelsMap[componentName] || formatLabelFromComponentName(componentName);
  }

  function getFormSummary(formName, formData, labelsMap = {}) {
    if (formName.includes('FSBS')) {
      const reading = formData.find((d) => d.component_name === 'fsbsReading')?.value || '-';
      const isHypo = formData.find((d) => d.component_name === 'fsbsReason.hypoglycemic')?.value;
      const reasons = formData
        .filter((d) => d.component_name.startsWith('fsbsReason.') && d.value === true)
        .map((d) => getFieldLabel(d.component_name, labelsMap));

      const reasonSuffix = reasons.length ? ` (Reason: ${reasons.join(', ')})` : '';
      const hypoSuffix = isHypo ? ' (Hypoglycemic)' : '';

      if (reasonSuffix) {
        return `${reading} mg/dL${reasonSuffix}`;
      }

      if (hypoSuffix) {
        return `${reading} mg/dL${hypoSuffix}`;
      }

      return `${reading} mg/dL`;
    }

    if (formName.includes('Food')) {
      const foodItems = formData.filter((d) => d.component_name.includes('foodItems'));
      const foodSummary = [...Array(Math.floor(foodItems.length / 2))]
        .map((_, i) => {
          const foodItem =
            formData.find((d) => d.component_name === `foodItems[${i}].FoodItem`)?.value || '';
          const percentage = formData.find(
            (d) => d.component_name === `foodItems[${i}].FoodPercentage`
          )?.value;
          return `${foodItem.trim().replace(/^\w/, (char) => char.toUpperCase())}: ${percentage}%`;
        })
        .filter(Boolean)
        .join(', ');

      const restaurant = formData.find((d) => d.component_name === 'restaurant')?.value;
      const restaurantName = formData.find((d) => d.component_name === 'restaurantName')?.value;
      return `${foodSummary}${restaurant === 'yes' && restaurantName ? ` @ ${restaurantName}` : ''}`;
    }

    if (formName.includes('Starch')) {
      let starchType = '';
      if (formData.find((d) => d.component_name === 'StarchType.glycosade')?.value) {
        starchType = 'Glycosade';
      } else if (formData.find((d) => d.component_name === 'StarchType.cornstarch')?.value) {
        starchType = 'Cornstarch';
      } else if (formData.find((d) => d.component_name === 'StarchType.tapioca')?.value) {
        starchType = 'Tapioca';
      } else if (formData.find((d) => d.component_name === 'StarchType.other')?.value) {
        starchType = formData.find((d) => d.component_name === 'OtherStarch')?.value || '';
      }

      const grams = formData.find((d) => d.component_name === 'StarchGrams')?.value || '-';
      return `${starchType} ${grams}g`.trim();
    }

    if (formName.includes('Ketone')) {
      const value = formData.find((d) => d.component_name === 'KetoneValue')?.value || '-';
      const reasons = formData
        .filter((d) => d.component_name.startsWith('Reason.') && d.value === true)
        .map((d) => getFieldLabel(d.component_name, labelsMap));
      return `${value} mmol/L${reasons.length ? ` (Reason: ${reasons.join(', ')})` : ''}`;
    }

    if (formName.includes('Blood Glucose')) {
      const reading = formData.find((d) => d.component_name === 'fsbsReading')?.value || '-';
      return `${reading} mg/dL`;
    }

    if (formName.includes('Hypo')) {
      const otherComments = formData.find((d) => d.component_name === 'OtherComments')?.value || '';
      const arrayEntry = formData.find((d) => d._isArray && Array.isArray(d.value));
      if (arrayEntry?.value?.length) {
        const labels = arrayEntry.value.map((code) => {
          if (code === 'other' && otherComments) {
            return otherComments;
          }
          return getFieldLabel(`${arrayEntry.component_name}.${code}`, labelsMap);
        });
        if (otherComments && !arrayEntry.value.includes('other')) {
          labels.push(`Other: ${otherComments}`);
        }
        return labels.join(', ');
      }

      const selected = formData.filter((d) => d.value === true && !d._isArray);
      const labels = selected.map((d) => {
        if (d.component_name.endsWith('.other') && otherComments) {
          return otherComments;
        }
        return getFieldLabel(d.component_name, labelsMap);
      });

      if (otherComments && !selected.some((d) => d.component_name.endsWith('.other'))) {
        labels.push(`Other: ${otherComments}`);
      }

      return labels.length > 0 ? labels.join(', ') : 'Click to view details';
    }

    if (formName.includes('Insulin')) {
      const units = formData.find((d) => d.component_name === 'Units')?.value || '-';
      const drugName = formData.find((d) => d.component_name === 'nameOfDrug')?.value || '';
      const routeDropdown = formData.find(
        (d) => d.component_name === 'Route' && typeof d.value === 'string'
      );
      const routeEntry = formData.find(
        (d) => d.component_name.startsWith('Route.insulin') && d.value === true
      );

      let route = '';
      if (routeDropdown?.value) {
        route =
          labelsMap[`Route.${routeDropdown.value}`] ||
          routeDropdown.value
            .replace(/^insulin/, '')
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/^./, (str) => str.toUpperCase())
            .trim();
      } else if (routeEntry) {
        route =
          labelsMap[routeEntry.component_name] ||
          routeEntry.component_name
            .replace('Route.', '')
            .replace(/^insulin/, '')
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/^./, (str) => str.toUpperCase())
            .trim();
      }

      const parts = [];
      if (units !== '-') parts.push(`${units} units`);
      if (drugName) parts.push(drugName);
      if (route) parts.push(route);
      return parts.length > 0 ? parts.join(', ') : 'Click to view details';
    }

    if (formName.includes('Immunosuppression')) {
      return formData
        .filter((d) => d.value === true)
        .map((d) => d.component_name.split('.')[1])
        .filter(Boolean)
        .map((name) => name.charAt(0).toUpperCase() + name.slice(1))
        .join(', ');
    }

    return 'Click to view details';
  }

  function parseFormProperties(properties) {
    try {
      if (!properties) {
        return [];
      }
      return flattenData(JSON.parse(sanitizeJsonString(properties)));
    } catch (error) {
      console.error('Error parsing form properties:', error);
      return [];
    }
  }

  function formatDetailValue(value, timezone = 'UTC') {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return formatToLocalDateTime(value.toISOString(), timezone);
    }

    if (Array.isArray(value)) {
      return value.join(', ');
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    return String(value);
  }

  function buildFormContentItems(formData, labelsMap = {}, timezone = 'UTC') {
    const items = [];
    const seen = new Set();

    formData.forEach((entry) => {
      if (!entry?.component_name || seen.has(entry.component_name)) {
        return;
      }

      if (entry.value === false || entry.value === null || entry.value === undefined || entry.value === '') {
        return;
      }

      if (entry.component_name.startsWith('foodItems[')) {
        return;
      }

      if (entry.component_name.includes('.other') && entry.value === true) {
        return;
      }

      if (entry.value === true && entry.component_name.includes('.')) {
        items.push({
          label: 'Selected',
          value: getFieldLabel(entry.component_name, labelsMap),
        });
        seen.add(entry.component_name);
        return;
      }

      const formattedValue = formatDetailValue(entry.value, timezone);
      if (!formattedValue) {
        return;
      }

      items.push({
        label: getFieldLabel(entry.component_name, labelsMap),
        value: formattedValue,
      });
      seen.add(entry.component_name);
    });

    return items.slice(0, 10);
  }

  function extractDateFromProperties(formName, formData) {
    let dateValue = null;

    if (formName.includes('FSBS')) {
      dateValue = formData.find((d) => d.component_name === 'fsbsDate')?.value;
    } else if (formName.includes('Food')) {
      dateValue = formData.find((d) => d.component_name === 'FoodIntakeDate')?.value;
    } else if (formName.includes('Starch')) {
      dateValue = formData.find((d) => d.component_name === 'StarchDate')?.value;
    } else if (formName.includes('Ketone')) {
      dateValue = formData.find((d) => d.component_name === 'KetoneDate')?.value;
    } else if (formName.includes('Symptoms')) {
      dateValue = formData.find((d) => d.component_name === 'SymptomDate')?.value;
    } else if (formName.includes('Action') && !formName.includes('Actions')) {
      dateValue = formData.find((d) => d.component_name === 'ActionDate')?.value;
    }

    if (!dateValue) {
      dateValue = formData.find((d) => d.component_name === 'Date')?.value;
    }

    return dateValue;
  }

  function toUtcIsoFromTimezoneLocal(rawValue, timeZone) {
    if (!rawValue) return null;

    if (rawValue instanceof Date) {
      return Number.isNaN(rawValue.getTime()) ? null : rawValue.toISOString();
    }

    if (typeof rawValue !== 'string') {
      return null;
    }

    const trimmedValue = rawValue.trim();
    if (!trimmedValue) {
      return null;
    }

    const directDate = new Date(trimmedValue);
    if (
      /[zZ]|[+-]\d{2}:\d{2}$/.test(trimmedValue) &&
      !Number.isNaN(directDate.getTime())
    ) {
      return directDate.toISOString();
    }

    let normalizedValue = trimmedValue;
    if (/^\d{4}-\d{2}-\d{2}$/.test(normalizedValue)) {
      normalizedValue = `${normalizedValue}T12:00:00`;
    } else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(normalizedValue)) {
      normalizedValue = `${normalizedValue}:00`;
    }

    const [datePart, timePartRaw] = normalizedValue.split('T');
    if (!datePart || !timePartRaw) {
      return !Number.isNaN(directDate.getTime()) ? directDate.toISOString() : null;
    }

    const [y, m, d] = datePart.split('-').map((n) => parseInt(n, 10));
    const [hh, mm, ss] = timePartRaw.split(':').map((n) => parseInt(n, 10));
    const sec = Number.isNaN(ss) ? 0 : ss;

    const utcGuessMs = Date.UTC(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, sec);
    const guessDate = new Date(utcGuessMs);

    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone || 'UTC',
      timeZoneName: 'shortOffset',
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    const parts = fmt.formatToParts(guessDate);
    const tzName = parts.find((p) => p.type === 'timeZoneName')?.value || '';
    const match = tzName.match(/([+-])(\d{1,2}):(\d{2})/);

    let offset = 'Z';
    if (match) {
      const sign = match[1];
      const hhOff = match[2].padStart(2, '0');
      const mmOff = match[3].padStart(2, '0');
      offset = `${sign}${hhOff}:${mmOff}`;
    }

    const yStr = String(y).padStart(4, '0');
    const mStr = String(m).padStart(2, '0');
    const dStr = String(d).padStart(2, '0');
    const hhStr = String(hh || 0).padStart(2, '0');
    const mmStr = String(mm || 0).padStart(2, '0');
    const ssStr = String(sec).padStart(2, '0');
    const isoWithOffset = `${yStr}-${mStr}-${dStr}T${hhStr}:${mmStr}:${ssStr}${offset}`;
    const resolvedDate = new Date(isoWithOffset);

    return Number.isNaN(resolvedDate.getTime()) ? null : resolvedDate.toISOString();
  }

  function resolveFormEventTimestamp(form, formData, timezone) {
    const propertiesDate = extractDateFromProperties(form?.name || '', formData);

    if (formDatePriority === 'properties') {
      return (
        toUtcIsoFromTimezoneLocal(propertiesDate, timezone) ||
        toUtcIsoFromTimezoneLocal(form?.formDate, timezone) ||
        form?.received ||
        null
      );
    }

    return (
      toUtcIsoFromTimezoneLocal(form?.formDate, timezone) ||
      toUtcIsoFromTimezoneLocal(propertiesDate, timezone) ||
      form?.received ||
      null
    );
  }

  function createFormEvent(form, index = 0) {
    const timezone = form?.eventTimezone || form?.timezone || $selectedParticipant?.timezoneCode || 'UTC';
    const formData = form?.parsedProperties || parseFormProperties(form?.properties);
    const labelsMap = getEnglishLabelsFromFD(form?.definition);
    const summary = getFormSummary(form?.name || '', formData, labelsMap);
    const normalizedSummary = summary === 'Click to view details' ? '' : summary;
    const contentItems = buildFormContentItems(formData, labelsMap, timezone);
    const timestamp = form?.eventTimestamp || resolveFormEventTimestamp(form, formData, timezone);

    return {
      id: `form-${timestamp || form?.received || 'unknown'}-${index}`,
      kind: 'form',
      typeLabel: 'Form Entry',
      sourceLabel: 'Diary',
      title: form?.name || 'Form entry',
      summary: normalizedSummary,
      description:
        normalizedSummary
          ? normalizedSummary
          : form?.name
            ? `Participant submitted the "${form.name}" form entry.`
            : 'Participant submitted a form entry.',
      contentItems,
      timestamp,
      timezone,
    };
  }

  function createMessageEvent(message, index = 0) {
    return {
      id: `message-${message?.sent || 'unknown'}-${index}`,
      kind: 'message',
      typeLabel: 'Alert / Message',
      sourceLabel: 'Messaging',
      title: message?.title || 'Alert / Message',
      summary: message?.message || '',
      description: message?.message || 'No additional event details are available.',
      contentItems: message?.message
        ? [
            {
              label: 'Message',
              value: message.message,
            },
          ]
        : [],
      timestamp: message?.sent,
    };
  }

  function buildEvents(formsData = [], messagesData = []) {
    const formEvents = formsData
      .filter((form) => form?.eventTimestamp)
      .map((form, index) => createFormEvent(form, index));

    const messageEvents = messagesData
      .filter((message) => message?.sent)
      .map((message, index) => createMessageEvent(message, index));

    return [...formEvents, ...messageEvents].sort(
      (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
    );
  }

  function getDateInTimezone(timestamp, timezone) {
    if (!timestamp || !timezone) {
      return '';
    }

    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date(timestamp));

    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    const day = parts.find((part) => part.type === 'day')?.value;

    return year && month && day ? `${year}-${month}-${day}` : '';
  }

  function isOnSelectedDate(timestamp, timezone) {
    if (!timestamp || !timezone || !selectedDate) {
      return false;
    }

    return getDateInTimezone(timestamp, timezone) === selectedDate;
  }

  function formatEventTime(timestamp) {
    if (!timestamp || !$selectedParticipant?.timezoneCode) {
      return '';
    }

    return new Date(timestamp).toLocaleTimeString('en-US', {
      timeZone: $selectedParticipant.timezoneCode,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  }

  function isBackfilledReading(raw) {
    if (!raw || typeof raw !== 'string') {
      return false;
    }

    return raw.includes('"isBackfilled":true') || raw.includes('"wasBackfilled":true');
  }

  function truncateText(value, maxLength = 120) {
    if (!value) {
      return '';
    }

    return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;
  }

  $: events = buildEvents(forms, messages);
  $: activeTableCount =
    isEventTimelineEnabled && activeTableView === TABLE_VIEWS.EVENTS ? events.length : compliancesCount;

  async function fetchFormsMessages(page = 1) {
    try {
      let url = `${API_BASE_URL}/forms/userForms?page=1&pageSize=9999&subjectGUID=${$selectedParticipant._id}&day=${selectedDate}&timezone=${$selectedParticipant.timezoneCode}`;

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch forms');
      }

      const responseData = await res.json();
      forms = responseData.data
        .map((form) => {
          const timezone =
            form.F.timezone || form.TZ?.code || $selectedParticipant?.timezoneCode || 'UTC';
          const parsedProperties = parseFormProperties(form.F.properties);
          const eventTimestamp = resolveFormEventTimestamp(
            {
              name: form.F.name,
              formDate: form.F.formDate,
              received: form.F.received,
            },
            parsedProperties,
            timezone
          );

          return {
            name: form.F.name,
            received: form.F.received,
            formDate: form.F.formDate,
            properties: form.F.properties,
            parsedProperties,
            definition: form.FD,
            timezone,
            eventTimezone: timezone,
            eventTimestamp,
          };
        })
        .filter((form) => isOnSelectedDate(form.eventTimestamp, form.eventTimezone));

      //console.log("forms:", JSON.stringify(forms[0], null, 2));
    } catch (err) {
      console.error('Error fetching log entries:', err);
      forms = [];
    }

    try {
      let url = `${API_BASE_URL}/messages?page=1&pageSize=9999&subjectGUID=${$selectedParticipant._id}&day=${selectedDate}&timezone=${$selectedParticipant.timezoneCode}`;

      //console.log("selectedDate:", selectedDate);
      //console.log("timezone:", $selectedParticipant.timezoneCode);

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch messages');
      }

      const responseData = await res.json();
      messages = responseData.data.filter((message) =>
        isOnSelectedDate(message?.sent, $selectedParticipant?.timezoneCode || 'UTC')
      );

      //console.log("messages:", messages);
    } catch (err) {
      console.error('Error fetching log entries:', err);
      messages = [];
    }

    await fetchChartCGMs();
  }

  async function fetchCGMs(page = 1) {
    try {
      let url = `${API_BASE_URL}/glucoseReadings?page=${page}&pageSize=${limit}&subjectGUID=${$selectedParticipant._id}&type=${selectedType}&inRange=${inRange}`;

      if (selectedDate) {
        url += `&day=${selectedDate}&timezone=${$selectedParticipant.timezoneCode}`;
      }

      if ($delayHours > 0) {
        url += `&delayHours=${$delayHours}`;
      }

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch cgm');
      }

      const data = await res.json();

      cgms = data.data || []; // Ensure cgms is an array
      compliancesCount = data.totalCount || 0;
      currentPage = data.page || 1;
      totalPages = data.totalPages || 1;

      //console.log("cgms:", cgms);
    } catch (err) {
      console.error('Error fetching cgms:', err);
      cgms = []; // Set to empty array in case of error
    }
  }

  async function fetchChartCGMs() {
    if (!selectedDate) {
      scatterData = [];
      return;
    }

    try {
      isLoading = true;

      let url = `${API_BASE_URL}/glucoseReadings?page=1&pageSize=9999&subjectGUID=${$selectedParticipant._id}&type=${selectedType}&inRange=${inRange}&day=${selectedDate}&timezone=${$selectedParticipant.timezoneCode}`;

      if ($delayHours > 0) {
        url += `&delayHours=${$delayHours}`;
      }

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch cgm');
      }

      const data = await res.json();
      chartCgms = data.data || [];

      // Sort readings by timestamp
      chartCgms.sort((a, b) => new Date(a.read) - new Date(b.read));

      scatterData = [];

      // Create start and end times for the selected date
      const startTime = new Date(`${selectedDate}T00:00:00`);
      const endTime = new Date(`${selectedDate}T23:59:59.999`);

      // Create start and end of day timestamps in participant's timezone
      const startOfDay = new Date(
        startTime.toLocaleString('en-US', {
          timeZone: $selectedParticipant.timezoneCode,
        })
      );
      const endOfDay = new Date(
        endTime.toLocaleString('en-US', {
          timeZone: $selectedParticipant.timezoneCode,
        })
      );

      // If there's no data, add boundary points
      if (chartCgms.length === 0) {
        scatterData = [
          { x: startOfDay, y: null, type: 'gap' },
          { x: endOfDay, y: null, type: 'gap' },
        ];
      } else {
        // Add start boundary if needed
        const firstReading = new Date(
          new Date(chartCgms[0].read).toLocaleString('en-US', {
            timeZone: $selectedParticipant.timezoneCode,
          })
        );

        if (firstReading > startOfDay) {
          scatterData.push({
            x: startOfDay,
            y: null,
            type: 'gap',
          });
        }

        // Process readings
        for (let i = 0; i < chartCgms.length; i++) {
          const currentReading = chartCgms[i];
          const nextReading = chartCgms[i + 1];

          // Convert timestamp to participant's timezone
          const readingTime = new Date(
            new Date(currentReading.read).toLocaleString('en-US', {
              timeZone: $selectedParticipant.timezoneCode,
            })
          );

          // Add current reading with raw mg/dL value (no conversion)
          scatterData.push({
            x: readingTime,
            y: currentReading.value ?? null,
            type: currentReading.type,
            sensorId: currentReading.externalId,
            backfilled: isBackfilledReading(currentReading.raw),
          });

          // Check for gaps
          if (nextReading) {
            const nextTime = new Date(
              new Date(nextReading.read).toLocaleString('en-US', {
                timeZone: $selectedParticipant.timezoneCode,
              })
            );

            const timeDiff = nextTime - readingTime;
            if (timeDiff > 6 * 60 * 1000) {
              scatterData.push({
                x: new Date(readingTime.getTime() + 1),
                y: null,
                type: 'gap',
              });
            }
          }
        }

        // Add end boundary if needed
        const lastReading = new Date(
          new Date(chartCgms[chartCgms.length - 1].read).toLocaleString('en-US', {
            timeZone: $selectedParticipant.timezoneCode,
          })
        );

        if (lastReading < endOfDay) {
          scatterData.push({
            x: endOfDay,
            y: null,
            type: 'gap',
          });
        }
      }

      // Add form events to scatter data
      forms.forEach((form, index) => {
        const event = createFormEvent(form, index);
        if (!event.timestamp) {
          return;
        }
        const formTime = new Date(
          new Date(event.timestamp).toLocaleString('en-US', {
            timeZone: event.timezone || form.eventTimezone || form.timezone || 'UTC',
          })
        );
        scatterData.push({
          x: formTime,
          y: 0,
          type: 'form',
          event,
        });
      });

      messages.forEach((message, index) => {
        if (message.sent) {
          const event = createMessageEvent(message, index);
          const messageTime = new Date(
            new Date(message.sent).toLocaleString('en-US', {
              timeZone: $selectedParticipant.timezoneCode,
            })
          );

          scatterData.push({
            x: messageTime,
            y: 0,
            type: 'message',
            event,
          });
        }
      });

      //console.log('scatterData:', scatterData);

      isLoading = false;
    } catch (err) {
      console.error('Error fetching cgms:', err);
      scatterData = [];
      isLoading = false;
    }
  }

  // SPTL-533: CGM export ceiling, per-study configurable like the diary
  // export (SPTL-484). Default 99,999 per PM direction.
  $: cgmExportMaxRows =
    $studyProperties?.reports?.reportSettings?.cgmExportMaxRows || 99999;

  async function fetchAllCGMs() {
    try {
      isLoading = true;

      let url = `${API_BASE_URL}/glucoseReadings?page=1&pageSize=${cgmExportMaxRows}&subjectGUID=${$selectedParticipant._id}&type=${selectedType}&inRange=${inRange}`;

      // SPTL-533: this block previously appended to an undeclared `url`
      // while the fetch used an inline string — a ReferenceError whenever
      // delayHours was set, which the catch turned into an empty export.
      if ($delayHours > 0) {
        url += `&delayHours=${$delayHours}`;
      }

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch all cgm');
      }

      const data = await res.json();

      isLoading = false;
      return data.data || [];
    } catch (err) {
      console.error('Error fetching all cgm:', err);
      isLoading = false;
      return [];
    }
  }

  async function fetchLastCgmDay() {
    try {
      let url = `${API_BASE_URL}/glucoseReadings/daily?page=1&pageSize=1&sortOrder=desc&subjectGUID=${$selectedParticipant._id}&timezone=${$selectedParticipant.timezoneCode}&type=CGM&inRange=false`;
      if ($delayHours > 0) {
        url += `&delayHours=${$delayHours}`;
      }
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error('Failed to fetch last CGM day');
      }
      const data = await res.json();
      const first = data?.data?.[0];
      return first?.day || '';
    } catch (err) {
      console.error('Error fetching last CGM day:', err);
      return '';
    }
  }

  onMount(async () => {
    // Get date from URL parameter or use participant's local date
    const urlDate = $page.url.searchParams.get('date');
    if (urlDate) {
      selectedDate = urlDate;
    } else {
      // Try to use the most recent day with CGM data; fall back to local date
      const lastCgmDay = await fetchLastCgmDay();
      if (lastCgmDay) {
        selectedDate = lastCgmDay;
      } else {
        const localDate = new Date().toLocaleDateString('en-US', {
          timeZone: $selectedParticipant.timezoneCode,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        });
        const [month, day, year] = localDate.split('/');
        selectedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }

      // Update URL with the local date
      const url = new URL(window.location);
      url.searchParams.set('date', selectedDate);
      goto(url.toString(), { replaceState: true });
    }

    fetchCGMs(1);
    //fetchChartCGMs();
    fetchFormsMessages(1);
  });

  async function handleExportCSV() {
    const allCGMs = await fetchAllCGMs();
    exportToCSV(allCGMs, 'cgms');
  }

  async function handleExportExcel() {
    const allCGMs = await fetchAllCGMs();

    const filename = generateExportFilename({
      protocolNumber: $selectedStudy?.protocolNumber,
      subjectId: $selectedParticipant?.subjectId,
      reportType: 'CGM',
    });

    exportToExcel(allCGMs, filename);
  }

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      fetchCGMs(newPage);
    }
  }

  function handleRefresh() {
    fetchCGMs(1);
    //fetchChartCGMs();
    fetchFormsMessages(1);
  }
  function handleChartVisibility() {
    chartVisible = !chartVisible;
  }

  function showModal(rawData) {
    selectedRawData = rawData;
    isModalOpen = true;
  }

  function closeModal() {
    isModalOpen = false;
    selectedRawData = '';
  }

  function shiftDate(days) {
    const d = new Date(selectedDate + 'T12:00:00');
    d.setDate(d.getDate() + days);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    selectedDate = `${year}-${month}-${day}`;
    handleDateChange();
  }

  function handleDateChange() {
    const url = new URL(window.location);
    if (selectedDate) {
      url.searchParams.set('date', selectedDate);
    } else {
      url.searchParams.delete('date');
    }
    goto(url.toString(), { replaceState: true });
    fetchCGMs(1);
    //fetchChartCGMs();
    fetchFormsMessages(1);
  }

  function handleEventClick(eventData) {
    selectedEvent = eventData;
    isEventModalOpen = true;
  }

  function handleEventHover(eventData) {
    hoveredEvent = eventData;
  }

  function clearEventHover() {
    hoveredEvent = null;
  }

  function closeEventModal() {
    isEventModalOpen = false;
    selectedEvent = null;
  }

  function handleFormSubmitted() {
    fetchFormsMessages(1);
  }
</script>

<StudyHeader />

<FeatureInfo
  title="Measurements"
  description="This page displays detailed glucose readings for the selected participant. You can view CGM and SMBG data by date, filter readings, 
  and toggle between chart and table views. The chart shows readings along with forms and messages from the same day. 
  The delay setting defines if the readings are delayed by a certain number of hours (3h for Europe)."
  marginBottom="mb-0"
/>

{#if $selectedParticipant}
  <ParticipantDetails showOptionsMenu={false} />
{/if}

{#if isSMBGEnabled && isDiaryEnabled && isMessagingEnabled && isMeasurementsReportCardViewEnabled}
  <DailyReadingsCards
    cgmCount={chartCgms.filter((c) => c.type === 'CGM').length}
    smbgCount={chartCgms.filter((c) => c.type !== 'CGM').length}
    formCount={forms.length}
    messageCount={messages.length}
  />
{/if}

<!-- Title and Buttons -->
<Toolbar
  title="Measurements"
  itemCountTitle={isEventTimelineEnabled && activeTableView === TABLE_VIEWS.EVENTS ? 'Events' : 'Items'}
  itemCount={activeTableCount}
>
  <div class="text-m flex items-center gap-2">
    <div class="flex items-center mx-2 gap-2">
      <span class="me-1">Date</span>
      <input
        type="date"
        bind:value={selectedDate}
        on:input={handleDateChange}
        class="h-6 w-28 px-1 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100"
      />
      <div class="flex items-center gap-1.5 print:hidden">
        <button
          on:click={() => shiftDate(-1)}
          class="pagination p-0"
          title="Previous day"
        >
          <FontAwesomeIcon icon={faPlay} flip="horizontal" />
        </button>
        <button
          on:click={() => {
            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');
            selectedDate = `${year}-${month}-${day}`;
            handleDateChange();
          }}
          class="pagination p-0"
          title="Set to today"
        >
          <FontAwesomeIcon icon={faCalendarDay} />
        </button>
        <button
          on:click={() => shiftDate(1)}
          class="pagination p-0"
          title="Next day"
        >
          <FontAwesomeIcon icon={faPlay} />
        </button>
      </div>
    </div>
    <!-- <div class="flex items-center mx-2">
      <span class="me-1">Type</span>
      <select
        bind:value={selectedType}
        on:change={() => handleRefresh()}
        class="form-select h-6 w-24 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
      >
        <option value="ALL">ALL</option>
        <option value="CGM">CGM</option>
        <option value="SMBG">SMBG</option>
      </select>
    </div> -->
    {#if $config.delayVisible}
      <div class="flex items-center mx-2 gap-1 print:hidden">
        <span class="me-1">Delay</span>
        <select
          bind:value={$delayHours}
          on:change={() => handleRefresh()}
          class="form-select h-6 w-24 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
        >
          <option value={0}>None</option>
          <option value={3}>3 Hours</option>
        </select>
      </div>
    {:else if $delayHours > 0}
      <div class="flex items-center mx-2">
        <span class="me-1">Delay {$delayHours} hours</span>
      </div>
    {/if}
    <div class="flex items-center mx-2 print:hidden">
      <input
        type="checkbox"
        bind:checked={inRange}
        on:change={() => handleRefresh()}
        class="w-4 h-6 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 dark:focus:ring-red-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
      />
      <span class="ms-2">Valid Range</span>
    </div>
    {#if isEventTimelineEnabled}
      <div class="flex items-center mx-2 gap-1">
        <span class="me-1">List</span>
        <select
          bind:value={activeTableView}
          class="form-select h-6 w-28 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
        >
          <option value={TABLE_VIEWS.MEASUREMENTS}>CGM / BGM</option>
          <option value={TABLE_VIEWS.EVENTS}>Events</option>
        </select>
      </div>
      <AddSubjectFormButton
        buttonClass="bg-stone-300 dark:bg-gray-600 text-black dark:text-white py-1 px-3 rounded-full text-xs flex items-center gap-1"
        on:submitted={handleFormSubmitted}
      />
    {/if}
    <!-- <span class="mx-2 text-sm">
      {activeTableCount}
      {isEventTimelineEnabled && activeTableView === TABLE_VIEWS.EVENTS ? 'Events' : 'Items'}
    </span> -->
    <button
      on:click={() => handleRefresh()}
      class="pagination mx-1 p-0 print:hidden"
      title="Refresh"
    >
      <FontAwesomeIcon icon={faRotate} />
    </button>
    <!-- <PrintButton /> -->
  </div>
  <!-- <div class="print:hidden">
    <button
      on:click={handleChartVisibility}
      class="pagination mx-1 p-0"
      title="Toggle chart visibility"
    >
      {#if chartVisible}
        <FontAwesomeIcon icon={faChevronDown} />
      {:else}
        <FontAwesomeIcon icon={faChevronUp} />
      {/if}
    </button>
  </div> -->
</Toolbar>

{#if chartVisible}
  <div class="scatter-chart-container mx-1 flex min-h-[400px] flex-row bg-stone-50/50 pb-5 dark:bg-gray-800/50">
    <div class="w-full" style="height: 400px;">
      {#if scatterData.length > 0}
        <ScatterChart
          data={scatterData}
          {selectedDate}
          hoveredEvent={hoveredEvent}
          onEventClick={handleEventClick}
          showEventMarkers={!isEventTimelineEnabled}
        />
      {:else}
        <div
          class="flex h-full items-center justify-center text-sm text-gray-500 dark:text-gray-400"
        >
          {isLoading ? 'Loading measurements...' : 'No measurements available for this day.'}
        </div>
      {/if}
    </div>
  </div>

{/if}

{#if isEventTimelineEnabled}
  <EventTimeline
    events={events}
    timezone={$selectedParticipant?.timezoneCode || 'UTC'}
    onEventClick={handleEventClick}
    onEventHover={handleEventHover}
    onEventLeave={clearEventHover}
  />
{/if}

<div class="px-1 pt-0">

  {#if !isEventTimelineEnabled || activeTableView === TABLE_VIEWS.MEASUREMENTS}
    <table class="daily-measurements-container min-w-full border-collapse table-auto">
      <thead>
        <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
          <th class="px-4 py-2 text-left bold align-top">Source</th>
          <th class="px-4 py-2 text-left bold align-top">Serial Nr</th>
          <th class="px-4 py-2 text-left bold align-top">Type</th>
          <th class="px-4 py-2 text-left bold align-top">Value</th>
          <th class="px-4 py-2 text-left bold align-top">Unit</th>
          <th class="px-4 py-2 text-left bold align-top">Read (Local)</th>
          <th class="px-4 py-2 text-left bold align-top">Received (Local)</th>
          <th class="px-4 py-2 text-left bold align-top">Timezone</th>
        </tr>
      </thead>
      <tbody>
        {#each cgms as cgm, index}
          <tr
            class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
              2 ===
            0
              ? 'bg-stone-50 dark:bg-gray-725'
              : 'bg-stone-75 dark:bg-gray-750'}"
          >
            <td class="px-4 py-2 print:py-1 align-top">{cgm.manufacturer} {cgm.model}</td>
            <td
              class="px-4 py-2 print:py-1 cursor-pointer align-top"
              on:click={() => showModal(cgm.raw)}>{cgm.externalId}</td
            >
            <td class="px-4 py-2 print:py-1 align-top"
              >{cgm.type === 'CGM' ? cgm.type : 'SMBG ' + cgm.type}
              {cgm.location ? cgm.location : ''}</td
            >
            <td class="px-4 py-2 print:py-1 align-top"
              >{#if cgm.raw && cgm.raw.includes('Warmup')}
                Warmup
              {:else if cgm.raw && cgm.raw.includes('Invalid')}
                Invalid
              {:else}
                {cgm.value ? convertGlucoseValue(cgm.value, $glucoseunit) : ''}{cgm.raw &&
                cgm.raw.includes('"isBackfilled":true')
                  ? ' (b)'
                  : ''}{cgm.raw && cgm.raw.includes('"wasBackfilled":true') ? ' (b)' : ''}
              {/if}</td
            >
            <td class="px-4 py-2 print:py-1 align-top">{$glucoseunit}</td>
            <td class="px-4 py-2 print:py-1 align-top"
              >{formatToLocalDateTime(cgm.read, $selectedParticipant.timezoneCode)}</td
            >
            <td class="px-4 py-2 print:py-1 align-top"
              >{formatToLocalDateTime(cgm.received, $selectedParticipant.timezoneCode)}</td
            >
            <td class="px-4 py-2 print:py-1 align-top break-word-on-print">{cgm.timezone}</td>
          </tr>
        {/each}
      </tbody>
    </table>
  {:else}
    <table class="min-w-full border-collapse table-auto">
      <thead>
        <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
          <th class="px-4 py-2 text-left bold align-top">Type</th>
          <th class="px-4 py-2 text-left bold align-top">Event</th>
          <th class="px-4 py-2 text-left bold align-top">Details</th>
          <th class="px-4 py-2 text-left bold align-top">Time (Local)</th>
          <th class="px-4 py-2 text-left bold align-top">Source</th>
        </tr>
      </thead>
      <tbody>
        {#if events.length === 0}
          <tr class="bg-stone-50 text-sm text-gray-600 dark:bg-gray-725 dark:text-gray-300">
            <td colspan="5" class="px-4 py-6 text-center">
              No alerts or form entries were recorded for this day.
            </td>
          </tr>
        {:else}
          {#each events as event, index}
            <tr
              class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
                2 ===
              0
                ? 'bg-stone-50 dark:bg-gray-725'
                : 'bg-stone-75 dark:bg-gray-750'}"
            >
              <td class="px-4 py-2 align-top">
                <div class="flex items-center gap-3">
                  <span
                    class={`flex h-8 w-8 items-center justify-center rounded-full ${
                      event.kind === 'form'
                        ? 'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-200'
                        : 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-100'
                    }`}
                  >
                    <FontAwesomeIcon icon={event.kind === 'form' ? faFileLines : faBell} />
                  </span>
                  <span>{event.typeLabel}</span>
                </div>
              </td>
              <td class="px-4 py-2 align-top">
                <button
                  type="button"
                  class="text-left font-medium text-sky-700 hover:text-sky-900 dark:text-sky-300 dark:hover:text-sky-200"
                  on:click={() => handleEventClick(event)}
                  on:mouseenter={() => handleEventHover(event)}
                  on:mouseleave={clearEventHover}
                  on:focus={() => handleEventHover(event)}
                  on:blur={clearEventHover}
                >
                  {event.title}
                </button>
              </td>
              <td class="px-4 py-2 align-top">{truncateText(event.summary || event.description)}</td>
              <td class="px-4 py-2 align-top">{formatEventTime(event.timestamp)}</td>
              <td class="px-4 py-2 align-top">{event.sourceLabel}</td>
            </tr>
          {/each}
        {/if}
      </tbody>
    </table>
  {/if}
</div>

<!-- Add Export Buttons -->
<Footer>
  {#if !isEventTimelineEnabled || activeTableView === TABLE_VIEWS.MEASUREMENTS}
    <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
  {:else}
    <div class="px-2 text-xs text-gray-500 dark:text-gray-400">
      {events.length} {events.length === 1 ? 'event' : 'events'} on the selected day
    </div>
  {/if}
  <div>
    <button
      hidden
      on:click={handleExportCSV}
      class="bg-stone-300 dark:bg-gray-600 text-black dark:text-white py-1 px-4 rounded-full text-xs"
      >CSV</button
    >
    <button
      hidden
      on:click={handleExportExcel}
      class="bg-stone-300 dark:bg-gray-600 text-black dark:text-white py-1 px-4 rounded-full text-xs"
      >Excel</button
    >
  </div>
</Footer>

<RawModal {isModalOpen} rawData={selectedRawData} on:close={closeModal} />

<EventDetailsModal
  event={selectedEvent}
  isOpen={isEventModalOpen}
  timezone={$selectedParticipant?.timezoneCode || 'UTC'}
  on:close={closeEventModal}
/>

<style>
  /* Ensure all chart canvases have white background in print mode */
  @media print {
    canvas {
      background-color: white !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      color-adjust: exact;
    }
  }
</style>
